import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { 
  LayoutDashboard, 
  Building2, 
  TrendingUp, 
  Lightbulb, 
  AlertTriangle, 
  Award,
  Settings,
  LogOut,
  Users,
  Search,
  BarChart3,
  GitCompare,
  Bell
} from 'lucide-react'

const businessNavigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard
  },
  {
    name: 'Business Profile',
    href: '/business',
    icon: Building2
  },
  {
    name: 'Score History',
    href: '/score-history',
    icon: TrendingUp
  },
  {
    name: 'Recommendations',
    href: '/recommendations',
    icon: Lightbulb
  },
  {
    name: 'Alerts',
    href: '/alerts',
    icon: AlertTriangle
  },
  {
    name: 'Badges',
    href: '/badges',
    icon: Award
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings
  }
]

const lenderNavigation = [
  {
    name: 'Lender Dashboard',
    href: '/lender/dashboard',
    icon: LayoutDashboard
  },
  {
    name: 'Business Search',
    href: '/lender/search',
    icon: Search
  },
  {
    name: 'Score Comparison',
    href: '/lender/compare',
    icon: GitCompare
  },
  {
    name: 'Portfolio',
    href: '/lender/portfolio',
    icon: BarChart3
  },
  {
    name: 'Risk Assessment',
    href: '/lender/risk-assessment',
    icon: AlertTriangle
  },
  {
    name: 'Alert Subscriptions',
    href: '/lender/alerts',
    icon: Bell
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings
  }
]

export function DesktopSidebar() {
  const { user, logout } = useAuth()
  const location = useLocation()
  
  const isLenderPortal = location.pathname.startsWith('/lender')
  const navigation = isLenderPortal ? lenderNavigation : businessNavigation

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
      <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">TL</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                TrustLink
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isLenderPortal ? 'Lender Portal' : 'Business Health'}
              </p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400 capitalize">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                {item.name}
              </NavLink>
            )
          })}
        </nav>

        {/* Portal Switcher */}
        {user?.role === 'lender' && (
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Switch Portal
              </p>
              <NavLink
                to={isLenderPortal ? '/dashboard' : '/lender/dashboard'}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-md transition-colors"
              >
                <Users className="w-5 h-5 mr-3" />
                {isLenderPortal ? 'Business Portal' : 'Lender Portal'}
              </NavLink>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-md transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}